import Papa from "papaparse";
import { aggregateToWeeklyRows, buildRctInputFile } from "../../rct-experiment/components/rctExperimentApi";

/**
 * Causal-Loop-RCT-only data preparation. Neither function is called by, or
 * changes anything about, the standalone Bid Optimizer upload flow
 * (StepThree.tsx) or the standalone RCT Experiment upload flow (SetupStep.tsx)
 * - those keep uploading their own file exactly as they always have.
 *
 * Causal Loop (RCT) shares ONE raw file between both engines, but the two
 * engines need different shapes of it - same reasoning as
 * causal-loop/utils/engineDataExtraction.ts's extractSyntheticControlInput:
 *   - Bid Optimizer wants every segment row as-is (age/gender/device
 *     combinations per location per date) to compute per-segment multipliers.
 *   - The RCT 50/50 design engine (rct_5050_split.py / rct_common.py) expects
 *     one weekly row per location, matching exactly what the standalone RCT
 *     Experiment feature's own upload step already produces (see
 *     rctExperimentApi.ts's aggregateToWeeklyRows/buildRctInputFile) - reused
 *     here rather than reimplemented, so both features stay in sync with any
 *     future change to that aggregation logic.
 */

/** Bid Optimizer wants the full segment-level file unchanged - identity
 * function, kept as an explicit named seam (matching extractRctInput) rather
 * than skipping straight to the raw File object. */
export function extractBidOptimizerInput(file: File): File {
  return file;
}

/** Parses the shared raw file and aggregates it into the weekly
 * date/location/Y shape the RCT design engine has always expected -
 * identical aggregation to the standalone RCT Experiment feature's own
 * upload step, just fed from the shared Causal Loop file's chosen columns
 * instead of column auto-detection. */
export function extractRctInput(
  file: File,
  dateColumn: string,
  locationColumn: string,
  targetColumn: string
): Promise<File> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const weeklyRows = aggregateToWeeklyRows(
            results.data,
            dateColumn,
            locationColumn,
            targetColumn
          );
          resolve(buildRctInputFile(weeklyRows, "rct_experiment_data.csv"));
        } catch (err) {
          reject(err);
        }
      },
      error: (err: Error) => reject(err),
    });
  });
}
